import { type ComponentProps, type FC, useState } from 'react';
import { MultiSelectAutosuggest } from '../MultiSelectAutosuggest';
import { fuzzyFilter } from './fuzzyFilter';
import {
  Box,
  Columns,
  Column,
  Bleed,
  Text,
  IconGlobe,
  Loader,
  ButtonIcon,
  IconClear,
} from 'braid-design-system';
import { XSmallCheckbox } from 'src/libs/XSmallCheckbox';
import * as styles from './styles.css';
import translations from './.vocab';
import { useTranslations } from '@vocab/react';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';

export interface NationalityFacet {
  id: string;
  name: string;
  count: number;
}

interface TrackingChange {
  type: 'Selected' | 'Deselected';
  item: NationalityFacet;
}

interface NationalityFilterProps {
  hideLabel: boolean;
  nationalityFacets: NationalityFacet[];
  selectedNationalityFacets: NationalityFacet[];
  setSelectedNationalityFacets: (props: {
    selectedNationalityFacets: NationalityFacet[];
    changes: TrackingChange[];
  }) => void;
  dropdownBoxProps: ComponentProps<typeof Box>;
  loading: boolean;
}

const createItemCountMap = (facets: NationalityFacet[]) =>
  facets.reduce<Record<string, number>>((map, facet) => {
    map[facet.id] = facet.count;
    return map;
  }, {});

export const NationalityFilter: FC<NationalityFilterProps> = ({
  nationalityFacets,
  dropdownBoxProps,
  hideLabel,
  loading,
  selectedNationalityFacets,
  setSelectedNationalityFacets,
}) => {
  const { t } = useTranslations(translations);
  const { language } = useAppConfig();
  const sortAlphabetically = (a: NationalityFacet, b: NationalityFacet) =>
    a.name.localeCompare(b.name, language);

  const [searchText, setSearchText] = useState<string>('');

  const sortedNationalityFacets = nationalityFacets.sort(sortAlphabetically);
  const selectedItemCountMap = createItemCountMap(nationalityFacets);
  const filteredSuggestions =
    searchText.length === 0
      ? sortedNationalityFacets
      : fuzzyFilter({
          items: nationalityFacets,
          searchText,
        });

  const id = 'nationality-filter';

  const baseBoxProps = {
    background: 'surface',
    borderRadius: 'standard',
    width: 'full',
  } as const;

  if (loading) {
    return (
      <MultiSelectAutosuggest
        id={id}
        label={t('Nationality')}
        placeholder={t('Search applications by nationality')}
        suggestions={[]}
        selectedItems={[]}
        searchText=""
        setSearchText={() => {}}
        onItemsSelected={() => {}}
        icon={<IconGlobe />}
        hideLabel={hideLabel}
        dropdownBoxProps={{ ...baseBoxProps, ...dropdownBoxProps }}
        renderSuggestions={() => (
          <Box width="full" padding="small">
            <Loader size="small" aria-label={t('Loading nationalities')} />
          </Box>
        )}
        onClickSuggestionItem={() => {}}
      />
    );
  }

  const onClickSuggestionItem = (itemClicked: NationalityFacet): void => {
    const isSelected = selectedNationalityFacets.some(
      (item) => itemClicked.id === item.id,
    );
    if (isSelected) {
      setSelectedNationalityFacets({
        changes: [{ type: 'Deselected', item: itemClicked }],
        selectedNationalityFacets: selectedNationalityFacets.filter(
          (selectedItem) => selectedItem.id !== itemClicked.id,
        ),
      });
    } else {
      setSelectedNationalityFacets({
        changes: [{ type: 'Selected', item: itemClicked }],
        selectedNationalityFacets: [...selectedNationalityFacets, itemClicked],
      });

      setSearchText('');
    }
  };
  return (
    <MultiSelectAutosuggest
      id={id}
      label={t('Nationality')}
      placeholder={t('Search applications by nationality')}
      hideLabel={hideLabel}
      dropdownBoxProps={{ ...baseBoxProps, ...dropdownBoxProps }}
      suggestions={filteredSuggestions}
      selectedItems={selectedNationalityFacets}
      searchText={searchText}
      setSearchText={(newSearchText) => {
        setSearchText(newSearchText);
      }}
      onItemsSelected={(newSelectedItems) => {
        const changes: TrackingChange[] = [];
        newSelectedItems.forEach((item) => {
          if (
            !selectedNationalityFacets.find(
              (selectedNationality) => selectedNationality.id === item.id,
            )
          ) {
            changes.push({
              item,
              type: 'Selected',
            });
          }
        });
        selectedNationalityFacets.forEach((selectedNationaity) => {
          if (
            !newSelectedItems.find((item) => selectedNationaity.id === item.id)
          ) {
            changes.push({
              item: selectedNationaity,
              type: 'Deselected',
            });
          }
        });
        setSelectedNationalityFacets({
          changes,
          selectedNationalityFacets: newSelectedItems,
        });
        setSearchText('');
      }}
      renderSelectedItems={({ getSelectedItemProps }) => (
        <Box
          component="ul"
          display="flex"
          flexWrap="wrap"
          overflow="hidden"
          alignItems="center"
          className={styles.xxsmallGap}
        >
          <Text
            size="small"
            baseline={false}
            tone={
              selectedNationalityFacets.length > 0 || searchText
                ? 'neutral'
                : 'secondary'
            }
          >
            <IconGlobe />
          </Text>
          {selectedNationalityFacets.map((item, index) => {
            const count = selectedItemCountMap[item.id] ?? 0;
            const nationalityCountry = item.name;
            return (
              <Box
                key={`selected-item-${item.id}`}
                component="li"
                display="flex"
                flexWrap="nowrap"
                overflow="hidden"
                position="relative"
                alignItems="center"
                justifyContent="center"
                paddingY="xxsmall"
                paddingX="xsmall"
                background="neutralLight"
                borderRadius="full"
                className={[styles.xsmallGap, styles.textOverflowEllipsis]}
                aria-label={t('nationalityCountry count applications', {
                  count,
                  nationalityCountry,
                })}
                {...getSelectedItemProps({
                  selectedItem: item,
                  index,
                })}
              >
                <Box className={styles.tagText}>{nationalityCountry}</Box>

                <CountBadge count={count} isHighlighted />

                <ButtonIcon
                  id={`remove-${nationalityCountry}-btn`}
                  size="small"
                  icon={<IconClear />}
                  label={t('Clear {item}', { item: nationalityCountry })}
                  onClick={() => onClickSuggestionItem(item)}
                  variant="transparent"
                />
              </Box>
            );
          })}
        </Box>
      )}
      renderSuggestions={({ getItemProps, highlightedIndex }) =>
        filteredSuggestions.length > 0 ? (
          <>
            {filteredSuggestions.map((suggestion, itemIndex) => {
              const isHighlighted = highlightedIndex === itemIndex;
              const isSelected = selectedNationalityFacets.some(
                (item) => suggestion.id === item.id,
              );
              return (
                <Box
                  key={`suggestion-item-${suggestion.id}`}
                  component="li"
                  cursor="pointer"
                  padding="small"
                  background={isHighlighted ? 'formAccentSoftHover' : undefined}
                  {...getItemProps({
                    item: suggestion,
                    name: suggestion.name,
                    index: itemIndex,
                  })}
                >
                  <Columns space="small" alignY="top">
                    <Column>
                      <XSmallCheckbox
                        id={`suggestion-checkbox-${suggestion.id}`}
                        label={suggestion.name}
                        checked={isSelected}
                        // We are relying on the checkbox to propagate the click event to the parent Box with getItemProps so that the menu remains open after selection
                        onChange={() => {}}
                        // This stops the label from focusing on the checkbox which closes the dropdown
                        onClick={(e) => {
                          e.preventDefault();
                        }}
                        ariaLabel={t('nationalityCountry count applications', {
                          nationalityCountry: suggestion.name,
                          count: suggestion.count,
                        })}
                      />
                    </Column>
                    <Column width="content">
                      <CountBadge
                        isHighlighted={isHighlighted}
                        count={suggestion.count}
                      />
                    </Column>
                  </Columns>
                </Box>
              );
            })}
          </>
        ) : (
          <Box paddingX="small" paddingY="small" background="surface">
            <Text size="small">{t('No nationalities found')}</Text>
          </Box>
        )
      }
      onClickSuggestionItem={onClickSuggestionItem}
    />
  );
};

const CountBadge: FC<{ count: number; isHighlighted: boolean }> = ({
  count,
  isHighlighted,
}) => {
  const { t } = useTranslations(translations);
  return (
    <Bleed vertical="xxsmall">
      <Box
        paddingY="xxsmall"
        paddingX="xsmall"
        borderRadius="large"
        background={isHighlighted ? 'surface' : 'neutralLight'}
        aria-hidden
      >
        <Text size="xsmall" maxLines={1}>
          {t('count', {
            count,
          })}
        </Text>
      </Box>
    </Bleed>
  );
};
